import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGetTransportVehicles } from '~/api/facility';
import { CANNED_TALLY_VEHICLE_NAMES } from '~/utils/vehicleGroups';
import {
  usePagedLengths,
  type ProductLengthFilters,
} from '~/api/productLengths';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import type {
  SortInput,
  DeliveryMode,
  SellerComparisonFiltersInput,
  CannedTallyCandidateLengthsQuery,
} from '~/graphql/graphql';

const DEFAULT_CANNED_TALLY_VEHICLE_NAME = CANNED_TALLY_VEHICLE_NAMES[0];

export function defaultCannedTallyVehicleId(
  transportVehicles: readonly { id: string; name: string }[] | undefined
): string | null {
  const vehicle = (transportVehicles ?? []).find(
    (candidate) => candidate.name === DEFAULT_CANNED_TALLY_VEHICLE_NAME
  );
  return vehicle ? vehicle.id : null;
}

/**
 * The vehicle canned tallies are offered for: `preferredVehicleId` when set, else
 * the default canned tally vehicle.
 *
 * @example
 *   const transportVehicleId = useCannedTallyVehicleId(cart.transportVehicle?.id);
 */
export function useCannedTallyVehicleId(
  preferredVehicleId: string | null | undefined
): string | null {
  const { data } = useGetTransportVehicles();
  return (
    preferredVehicleId ||
    defaultCannedTallyVehicleId(data ? data.transportVehicles : undefined)
  );
}

export const platformProductCannedTalliesQuery = graphql(`
  query PlatformProductCannedTallies(
    $platformProductId: ID!
    $transportVehicleId: ID!
    $organizationId: ID
  ) {
    platformProductCannedTallies(
      platformProductId: $platformProductId
      transportVehicleId: $transportVehicleId
      organizationId: $organizationId
    ) {
      id
      name
      bundles {
        platformProductsLength {
          lengthDisplayValue
          piecesPerLf
          piecesPerBf
          piecesPerMbf
          piecesPerSf
          piecesPerMsf
          piecesPerCbm
          length {
            actualLengthInches
          }
        }
        bundleCount
      }
    }
  }
`);

export const useGetPlatformProductCannedTallies = (
  platformProductId: string | undefined,
  transportVehicleId: string | undefined,
  organizationId?: string | null
) => {
  return useGraphqlQuery(
    platformProductCannedTalliesQuery,
    [
      'platformProductCannedTallies',
      platformProductId ?? '',
      transportVehicleId ?? '',
      organizationId ?? '',
    ],
    {
      platformProductId: platformProductId as string,
      transportVehicleId: transportVehicleId as string,
      organizationId,
    },
    { enabled: !!platformProductId && !!transportVehicleId }
  );
};

export const platformProductCannedTalliesByOrganizationIdsQuery = graphql(`
  query PlatformProductCannedTalliesByOrganizationIds(
    $platformProductId: ID!
    $transportVehicleId: ID!
    $organizationIds: [ID!]!
  ) {
    platformProductCannedTalliesByOrganizationIds(
      platformProductId: $platformProductId
      transportVehicleId: $transportVehicleId
      organizationIds: $organizationIds
    ) {
      organizationId
      tallies {
        id
        name
        bundles {
          platformProductsLength {
            piecesPerLf
            piecesPerBf
            piecesPerMbf
            piecesPerSf
            piecesPerMsf
            piecesPerCbm
            length {
              actualLengthInches
            }
          }
          bundleCount
        }
      }
    }
  }
`);

export const useGetPlatformProductCannedTalliesByOrganizationIds = (
  platformProductId: string | undefined,
  transportVehicleId: string | undefined,
  organizationIds: string[],
  options?: { enabled?: boolean }
) => {
  return useGraphqlQuery(
    platformProductCannedTalliesByOrganizationIdsQuery,
    [
      'platformProductCannedTalliesByOrganizationIds',
      platformProductId ?? '',
      transportVehicleId ?? '',
      organizationIds.join(','),
    ],
    {
      platformProductId: platformProductId as string,
      transportVehicleId: transportVehicleId as string,
      organizationIds,
    },
    {
      enabled:
        !!platformProductId &&
        !!transportVehicleId &&
        organizationIds.length > 0 &&
        (options?.enabled ?? true),
    }
  );
};

const cannedTallyLengthsForCategoryQuery = graphql(`
  query CannedTallyLengthsForCategory($platformProductId: ID!) {
    cannedTallyLengthsForCategory(platformProductId: $platformProductId) {
      length {
        id
        displayValue
        actualLengthInches
      }
      platformProductLength {
        id
        isNominal
        length {
          nominalLengthInches
          actualLengthInches
        }
        platformProduct {
          nominalWidthInches
          actualWidthInches
          nominalThicknessInches
          actualThicknessInches
          actualLengthInches
          piecesPerBundle {
            value
            unit
          }
        }
      }
    }
  }
`);

export const useGetCannedTallyLengthsForCategory = (
  platformProductId: string | undefined
) => {
  return useGraphqlQuery(
    cannedTallyLengthsForCategoryQuery,
    ['cannedTallyLengthsForCategory', platformProductId ?? ''],
    { platformProductId: platformProductId as string },
    { enabled: !!platformProductId }
  );
};

const cannedTallyCandidateLengthsQuery = graphql(`
  query CannedTallyCandidateLengths(
    $platformProductId: ID!
    $search: String
    $pageInput: PageInput!
  ) {
    lengths: cannedTallyCandidateLengths(
      platformProductId: $platformProductId
      search: $search
      pageInput: $pageInput
    ) {
      content {
        length {
          id
          displayValue
          actualLengthInches
        }
        platformProductLength {
          id
          isNominal
          length {
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            piecesPerBundle {
              value
              unit
            }
          }
        }
      }
      paging {
        number
        size
        totalElements
        totalPages
      }
    }
  }
`);

export type PickableProductLengthOption =
  CannedTallyCandidateLengthsQuery['lengths']['content'][number] & {
    id: string;
  };

/**
 * One page of the lengths a product can still be quoted on, filtered by a search term.
 *
 * Shaped for `usePaginatedQuery`: it takes `(page, pageSize, filters, sort, options)` and
 * gives every candidate an `id` of its length id, which that hook needs to merge pages and
 * which a candidate has no top-level field for.
 *
 * @example
 * const { data } = usePaginatedQuery({
 *   fetchHook: useCannedTallyCandidateLengths,
 *   select: (data) => data?.lengths,
 *   options: { pageSize: 20, filters: { platformProductId, search } },
 * });
 */
export const useCannedTallyCandidateLengths = (
  page: number,
  pageSize: number,
  filters?: ProductLengthFilters,
  _sort?: RouteSort,
  options?: { enabled?: boolean }
) =>
  usePagedLengths(
    cannedTallyCandidateLengthsQuery,
    'cannedTallyCandidateLengths',
    page,
    pageSize,
    filters,
    options
  );

const cannedTallySellerGroupsQuery = graphql(`
  query CannedTallySellerGroupsForBuyer(
    $platformProductId: ID!
    $transportVehicleId: ID!
    $tallyName: String
    $filter: CannedTallySellerGroupFilterInput
    $comparisonFilters: SellerComparisonFiltersInput
    $pageInput: PageInput!
    $sort: [SortInput!]
  ) {
    cannedTallySellerGroupsForBuyer(
      platformProductId: $platformProductId
      transportVehicleId: $transportVehicleId
      tallyName: $tallyName
      filter: $filter
      comparisonFilters: $comparisonFilters
      pageInput: $pageInput
      sort: $sort
    ) {
      content {
        id
        sellerName
        facility {
          id
          code
          organization {
            id
            name
            sellingOrganizationCode
          }
        }
        currency
        avgPrice
        priceUnit
        totalBundles
        leadTimeDays
        hasBestPrice
        hasAnyPricedLength
        lengthBreakdown {
          ft
          actualLengthInches
          platformProductsLengthId
          hasInventory
          bundles
          pricePerBundle {
            amount
          }
        }
      }
      paging {
        number
        size
        totalElements
        totalPages
      }
    }
  }
`);

export interface CannedTallySellerGroupFilters {
  destinationLocationId?: string;
  deliveryMode?: DeliveryMode;
}

export interface CannedTallySellerGroupsFetchFilters extends CannedTallySellerGroupFilters {
  platformProductId?: string;
  transportVehicleId?: string;
  tallyName?: string;
  comparisonFilters?: SellerComparisonFiltersInput;
}

export const useCannedTallySellerGroupsFetch = (
  page: number,
  pageSize: number,
  filters?: CannedTallySellerGroupsFetchFilters,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const {
    platformProductId,
    transportVehicleId,
    tallyName,
    comparisonFilters,
    ...queryFilters
  } = filters ?? {};

  const graphqlSort: SortInput[] | undefined = sort?.map((s) => ({
    field: s.field,
    direction: s.direction,
  }));

  return useGraphqlQuery(
    cannedTallySellerGroupsQuery,
    [
      'cannedTallySellerGroupsForBuyer',
      platformProductId ?? '',
      transportVehicleId ?? '',
      tallyName ?? '',
      page,
      pageSize,
      JSON.stringify(queryFilters),
      JSON.stringify(comparisonFilters ?? {}),
      JSON.stringify(graphqlSort ?? []),
    ],
    {
      platformProductId: platformProductId as string,
      transportVehicleId: transportVehicleId as string,
      tallyName,
      filter: queryFilters,
      comparisonFilters,
      pageInput: { page, size: pageSize },
      sort: graphqlSort,
    },
    {
      ...options,
      enabled:
        !!platformProductId &&
        !!transportVehicleId &&
        (options?.enabled ?? true),
    }
  );
};
